import { paynowCode } from "@ohc/shared";
import { useEffect, useState } from "react";
import { formatMoney, ApiError, api } from "../api";
import { SelectField } from "../fields";
import { Alert, Loading, Modal, Working } from "../ui";
import { beginCheckout, type Catalog, type Checkout, type LocalSale, recordSale, type SaleLine, tillAccount } from "./engine";

/**
 * Taking the money (the checkout flow in the scope confirmation): the reference first,
 * then the payment confirmed.
 *
 * For PayNow the reference is what the staff type into OneCollect, so it is shown large
 * before anything else. For cash the screen works out the change. A customer who will
 * pay later leaves with an invoice: the sale is recorded, unpaid, and paid from today's
 * list when the money comes. A customer who walks away before any of that leaves nothing
 * behind but a skipped number, which is normal.
 */
export function PosCheckout({
  catalog,
  method,
  split = false,
  lines,
  seller,
  locationId,
  event,
  onClose,
  onDone,
  corrects,
  reserved,
  onReserve,
  earlier,
}: {
  readonly catalog: Catalog;
  readonly method: "cash" | "paynow";
  /** Paid partly in cash and the rest by PayNow: the cash part is asked first (D, 18 October 2026). */
  readonly split?: boolean;
  readonly lines: readonly SaleLine[];
  readonly seller: { id: string; name: string };
  readonly locationId: string | null;
  readonly event: string | null;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
  /** The sale this one puts right, if it is a correction, and who allowed it. */
  readonly corrects?: { readonly sale: LocalSale; readonly by: { id: string; name: string } };
  /**
   * The number this basket was already given, if the screen was opened before. A customer
   * may have paid by PayNow with it already, so going Back to add a photo must not lose it
   * (D, 9 October 2026: A63 paid, Back pressed, the sale went in under another number).
   */
  readonly reserved?: Checkout | undefined;
  readonly onReserve?: (checkout: Checkout) => void;
  /**
   * Keying in an earlier day from its photos (D, 10 October 2026): the sale goes straight
   * to the server on that day and time, and is numbered there, not by this iPad's count.
   */
  readonly earlier?: { readonly day: string; readonly time: string } | undefined;
}): React.ReactElement {
  const [checkout, setCheckout] = useState<Checkout | undefined>(reserved);
  const [error, setError] = useState<string | undefined>();
  const [received, setReceived] = useState("");
  const [discount, setDiscount] = useState("");
  /**
   * Which way the round figure goes. Off the lot is the ordinary discount; on top of it is
   * for while the shop sells at cost and the person at the table adds their margin by hand.
   */
  const [direction, setDirection] = useState<"off" | "on">("off");
  const [customer, setCustomer] = useState("");
  /** The PayNow payment the person at the table saw arrive and picked for this sale. */
  const [receiptId, setReceiptId] = useState<string | null>(null);
  /** Some of it paid in cash, the rest by PayNow (D, 18 October 2026: 800 in cash and the rest by PayNow, for one slab). */
  const [cashPart, setCashPart] = useState("");
  const [busy, setBusy] = useState(false);

  const deviceAccount = tillAccount(catalog, method);
  const choices = catalog.accounts.filter((account) => (method === "cash" ? account.kind === "cash" : account.kind === "bank"));
  const [accountId, setAccountId] = useState(deviceAccount ?? choices[0]?.id ?? "");

  useEffect(() => {
    if (earlier) {
      setCheckout({ reference: "", saleId: "" });
      return;
    }
    if (reserved) return;
    void beginCheckout(catalog.device.code)
      .then((made) => {
        setCheckout(made);
        onReserve?.(made);
      })
      .catch(() => setError("This iPad could not create a sale reference. Close and try again"));
  }, []);

  const subtotal = lines.reduce((sum, line) => sum + line.quantity * line.unitPriceCents, 0);
  /**
   * A basket of free gifts and nothing else (D, 2 October): there is no money to take, so
   * the screen asks for none. It is still saved under its own reference, paid, at nothing,
   * which is what lets the day say how many were given away and by whom.
   */
  const free = subtotal === 0;
  // A dollar figure off the lot, which is how OHC give a discount at the table. Anything
  // that is not an amount counts as none, and it can never take the sale below nothing.
  const typed = Math.round(Number(discount.replace(/[^\d.]/g, "")) * 100);
  const amountCents = discount.trim() && Number.isFinite(typed) && typed > 0 ? typed : 0;
  const discountCents = direction === "off" ? Math.min(amountCents, subtotal) : 0;
  const surchargeCents = direction === "on" ? amountCents : 0;
  const total = subtotal - discountCents + surchargeCents;
  const tooMuch = direction === "off" && discount.trim() !== "" && Number.isFinite(typed) && typed > subtotal;
  const receivedCents = Math.round(Number(received) * 100);
  const change = received.trim() && Number.isFinite(receivedCents) ? receivedCents - total : null;
  const quick = [total, ...[10, 20, 50, 100].map((note) => note * 100).filter((note) => note > total)].slice(0, 4);
  const typedCash = Math.round(Number(cashPart.replace(/[^\d.]/g, "")) * 100);
  const cashPartCents = method === "paynow" && cashPart.trim() && Number.isFinite(typedCash) && typedCash > 0 ? typedCash : 0;
  const cashPartBad = cashPartCents > 0 && cashPartCents >= total;
  const paynowCents = total - cashPartCents;
  const cashAccount = tillAccount(catalog, "cash");

  async function finish(paid: boolean): Promise<void> {
    if (!checkout) return;
    if (paid && !accountId) return setError(`Select the ${method === "cash" ? "cash" : "PayNow"} account`);
    if (paid && cashPartBad) return setError("The cash part is the whole sale. Pay it as cash instead");
    if (paid && split && cashPartCents === 0) return setError("Key in how much was paid in cash");
    if (paid && cashPartCents > 0 && !cashAccount) return setError("This iPad has no cash account set for the cash part");
    setBusy(true);
    if (earlier) {
      try {
        const made = await api.recordEarlierSale({
          day: earlier.day,
          time: earlier.time,
          lines: lines.map((line) => ({
            productId: line.productId ?? null,
            slabLotId: line.slabLotId ?? null,
            quantity: line.quantity,
            unitPriceCents: line.unitPriceCents,
            note: line.note ?? null,
          })),
          ...(discountCents ? { discountCents } : {}),
          ...(surchargeCents ? { surchargeCents } : {}),
          method,
          accountId,
          ...(cashPartCents ? { cashPartCents, cashAccountId: cashAccount } : {}),
          locationId,
          event,
          customerName: customer.trim() || null,
        });
        let failed = 0;
        for (const photo of lines.flatMap((line) => line.photos ?? [])) {
          await api.addSalePhoto(made.saleId, photo).catch(() => (failed += 1));
        }
        onDone(
          `${made.reference} recorded on ${earlier.day} at ${earlier.time}, ${method === "cash" ? "cash" : "PayNow"} ${formatMoney(total)}.` +
            (failed ? ` ${failed} photo${failed === 1 ? "" : "s"} could not be added: drop them onto the sale on Sales and buybacks.` : ""),
        );
      } catch (problem) {
        setError(problem instanceof ApiError ? problem.message : "The sale could not be saved. An earlier day needs a connection");
        setBusy(false);
      }
      return;
    }
    try {
      await recordSale({
        checkout,
        lines,
        seller,
        locationId,
        event,
        customerName: customer.trim() || null,
        discountCents,
        surchargeCents,
        payment: paid
          ? {
              method,
              accountId: accountId || null,
              ...(method === "paynow" && receiptId ? { receiptId } : {}),
              ...(cashPartCents ? { cashPartCents, cashAccountId: cashAccount } : {}),
            }
          : null,
        ...(corrects ? { corrects } : {}),
      });
      onDone(
        free && paid
          ? `${checkout.reference} recorded as a complimentary sale, no payment required.`
          : paid
          ? (cashPartCents
              ? `${checkout.reference} paid ${formatMoney(cashPartCents)} in cash and ${formatMoney(paynowCents)} by PayNow, ${formatMoney(total)} in all.`
              : `${checkout.reference} paid by ${method === "cash" ? "cash" : "PayNow"}, ${formatMoney(total)}.`) +
              (method === "cash" && change !== null && change > 0 ? ` Change due ${formatMoney(change)}.` : "")
          : `${checkout.reference} saved as unpaid for ${formatMoney(total)}. Record the payment under Today's sales when it is received.`,
      );
    } catch {
      setError("The sale could not be saved on this iPad. Try again");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title={free ? "Complimentary sale" : method === "cash" ? "Pay by cash" : split ? "Pay by PayNow and cash" : "Pay by PayNow"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light me-auto" onClick={onClose} disabled={busy}>
            Back
          </button>
          {free || earlier ? null : (
            <button type="button" className="ti-btn ti-btn-light" disabled={busy || !checkout} onClick={() => void finish(false)}>
              Save as unpaid
            </button>
          )}
          <button
            type="button"
            className="ti-btn ti-btn-success-full !py-3 !px-5"
            disabled={busy || !checkout || (method === "cash" && change !== null && change < 0)}
            onClick={() => void finish(true)}
          >
            {busy ? <Working label="Saving" /> : free ? "Complete complimentary sale" : "Confirm payment received"}
          </button>
        </>
      }
    >
      {!checkout ? (
        error ? <Alert tone="danger">{error}</Alert> : <Loading />
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <div className="md:col-span-6 col-span-12">
            {earlier ? (
              <>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-1">Earlier day</p>
                <p className="font-semibold text-[1.5rem] text-warning mb-1">
                  {new Date(`${earlier.day}T12:00:00+08:00`).toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}, {earlier.time}
                </p>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-4">Numbered on that day when it is saved.</p>
              </>
            ) : method === "paynow" && !free ? (
              /*
               * The short code, large, for the customer to type into the PayNow note (D,
               * 3 October). OCBC's alert emails are what normally tie a payment to its sale;
               * when they do not come, the code on the bank statement does it at the end of
               * the day, which the Bank page matches by itself. The full reference is below.
               */
              <>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-1">PayNow reference</p>
                <p className="font-semibold text-[3.5rem] leading-none tracking-wider text-primary mb-2">
                  {paynowCode(checkout.reference)}
                </p>
                <p className="text-[0.813rem] mb-1">
                  Ask the customer to enter <span className="font-semibold">{paynowCode(checkout.reference)}</span> in their
                  payment note or comments.
                </p>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-4">Sale reference {checkout.reference}</p>
              </>
            ) : (
              <>
                <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-1">Sale reference</p>
                <p className="font-semibold text-[2rem] tracking-wider text-primary mb-4">{checkout.reference}</p>
              </>
            )}
            <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-1">Total amount due</p>
            <p className={`font-semibold text-[2.25rem] mb-1${free ? " text-success" : ""}`}>{free ? "No charge" : formatMoney(total)}</p>
            {discountCents > 0 || surchargeCents > 0 ? (
              <p className="text-[0.813rem] mb-4">
                <span className="text-[#8c9097] dark:text-white/50">Subtotal {formatMoney(subtotal)}</span>
                {discountCents > 0 ? (
                  <span className="text-success ms-2">Discount {formatMoney(discountCents)}</span>
                ) : (
                  <span className="text-primary ms-2">Markup {formatMoney(surchargeCents)}</span>
                )}
              </p>
            ) : (
              <div className="mb-4" />
            )}
            <ul className="list-none mb-0 text-[0.813rem]">
              {lines.map((line) => (
                <li key={line.lineId} className="flex justify-between gap-3 py-1 border-b border-dashed border-defaultborder">
                  <span className="truncate">
                    {line.quantity} x {line.description}
                  </span>
                  <span>{line.unitPriceCents === 0 ? "No charge" : formatMoney(line.quantity * line.unitPriceCents)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-6 col-span-12">
            {error ? <Alert tone="danger">{error}</Alert> : null}
            {free ? (
              <p className="text-[0.938rem] mb-0">
                <i className="ri-gift-line text-success me-1" />
                No payment is required. Select Complete complimentary sale to record it in today's sales.
              </p>
            ) : method === "paynow" ? (
              <>
                {earlier ? (
                  <p className="text-[0.875rem] mb-0">
                    Paid by PayNow. Match it to its line on the OCBC statement in Bank reconciliation afterwards.
                  </p>
                ) : (
                  <Arrivals total={paynowCents} picked={receiptId} onPick={setReceiptId} code={paynowCode(checkout.reference)} />
                )}
                {/* Part in cash, the rest by PayNow, as one sale (D, 18 October 2026). */}
                <label htmlFor="cash-part" className={`form-label ${split ? "font-semibold text-[1rem]" : "mt-3"}`}>
                  {split ? "Cash received" : "Part paid in cash (optional)"}
                </label>
                <input
                  id="cash-part"
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  value={cashPart}
                  placeholder="0.00"
                  autoFocus={split}
                  onChange={(event) => setCashPart(event.target.value)}
                />
                {cashPartCents > 0 ? (
                  <p className={`text-[0.813rem] mt-1 mb-0 ${cashPartBad ? "text-danger" : ""}`}>
                    {cashPartBad
                      ? "That is the whole sale. Pay it as cash instead."
                      : `${formatMoney(cashPartCents)} in cash and ${formatMoney(paynowCents)} by PayNow.`}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <label htmlFor="cash-received" className="form-label">
                  Cash received
                </label>
                <input
                  id="cash-received"
                  type="text"
                  inputMode="decimal"
                  className="form-control !text-[1.5rem] !py-3"
                  value={received}
                  placeholder={formatMoney(total)}
                  onChange={(event) => setReceived(event.target.value)}
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {quick.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className="ti-btn ti-btn-light !mb-0 !py-3 !px-4"
                      onClick={() => setReceived((amount / 100).toFixed(2))}
                    >
                      {amount === total ? "Exact amount" : formatMoney(amount)}
                    </button>
                  ))}
                </div>
                {change !== null ? (
                  <p className={`mt-4 mb-0 font-semibold text-[1.5rem] ${change < 0 ? "text-danger" : "text-success"}`}>
                    {change < 0 ? `Short by ${formatMoney(-change)}` : `Change due ${formatMoney(change)}`}
                  </p>
                ) : null}
              </>
            )}

            <div className={free ? "hidden" : "mt-4"}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <label htmlFor="sale-discount" className="form-label !mb-0">
                  {direction === "off" ? "Discount on sale total" : "Markup on sale total"}
                </label>
                {/* The two ways a round figure can go, side by side, the chosen one filled. */}
                <div className="inline-flex rounded-md border border-defaultborder overflow-hidden" role="group" aria-label="Which way">
                  {(["off", "on"] as const).map((way) => (
                    <button
                      key={way}
                      type="button"
                      aria-pressed={direction === way}
                      className={`px-3 py-2 text-[0.813rem] font-semibold whitespace-nowrap ${
                        direction === way
                          ? way === "off"
                            ? "bg-success text-white"
                            : "bg-primary text-white"
                          : "bg-white dark:bg-bodybg text-defaulttextcolor"
                      }`}
                      onClick={() => setDirection(way)}
                    >
                      {way === "off" ? "Apply discount" : "Add markup"}
                    </button>
                  ))}
                </div>
              </div>
              {/*
                A dollar a button, one to ten, because that is the discount somebody gives
                across a table and a show is too fast to open a keypad for it. Pressing the
                same one again takes it off. Anything else is typed in the box.
              */}
              <div className="grid grid-cols-5 gap-2 mb-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((dollars) => {
                  const chosen = amountCents === dollars * 100;
                  return (
                    <button
                      key={dollars}
                      type="button"
                      aria-label={`${dollars} dollars ${direction === "off" ? "off" : "more"}`}
                      className={`ti-btn !m-0 !py-3 !text-[1.063rem] font-semibold ${
                        chosen ? (direction === "off" ? "ti-btn-success-full" : "ti-btn-primary-full") : "ti-btn-light"
                      }`}
                      onClick={() => setDiscount(chosen ? "" : String(dollars))}
                    >
                      {direction === "off" ? "−" : "+"}
                      {dollars}
                    </button>
                  );
                })}
              </div>
              <div className="input-group">
                <span className="input-group-text">S$</span>
                <input
                  id="sale-discount"
                  type="text"
                  inputMode="decimal"
                  className="form-control !text-[1.25rem] !py-2"
                  value={discount}
                  placeholder="Adjustment amount"
                  onChange={(event) => setDiscount(event.target.value)}
                />
              </div>
              {tooMuch ? (
                <p className="text-[0.75rem] text-warning mt-1 mb-0">
                  The discount is more than the sale total. The full {formatMoney(subtotal)} is discounted.
                </p>
              ) : null}
            </div>

            <div className={free && deviceAccount ? "hidden" : "mt-4"}>
              <label htmlFor="pay-account" className="form-label">
                {method === "cash" ? "Cash account" : "PayNow account"}
              </label>
              <SelectField
                id="pay-account"
                value={accountId}
                placeholder="Select account"
                options={choices.map((account) => ({ value: account.id, label: account.name }))}
                onChange={setAccountId}
              />
              {!deviceAccount ? (
                <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
                  This iPad has no default account configured. A system administrator sets it on the dashboard, under Devices.
                </p>
              ) : null}
            </div>
            <div className="mt-4">
              <label htmlFor="customer-name" className="form-label">
                Customer name
              </label>
              <input
                id="customer-name"
                type="text"
                className="form-control"
                value={customer}
                placeholder="Optional. Needed when saving as unpaid"
                onChange={(event) => setCustomer(event.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

interface Arrival {
  readonly eventId: string;
  readonly amountCents: number;
  readonly paidAt: string;
}

/**
 * The PayNow payments that have just come in, from OCBC's alert emails (Daren, 1 October
 * 2026). The customer pays the shop's PayNow QR; when the money lands it shows here within
 * moments, so the person at the table sees it arrive rather than taking the customer's
 * phone screen on trust. Picking it ties it to this sale. One of the right amount is picked
 * by itself; with two, the person picks. With no signal, or before the alerts are set up,
 * this says so and the payment is confirmed the way it always was.
 */
function Arrivals({
  total,
  picked,
  onPick,
  code,
}: {
  readonly total: number;
  readonly picked: string | null;
  readonly onPick: (eventId: string | null) => void;
  readonly code: string;
}): React.ReactElement {
  const [arrivals, setArrivals] = useState<Arrival[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [chosenByHand, setChosenByHand] = useState(false);

  useEffect(() => {
    let live = true;
    async function look(): Promise<void> {
      try {
        const response = await fetch("/api/paynow/receipts?minutes=30&open=1", { credentials: "same-origin", cache: "no-store" });
        if (!response.ok) throw new Error(String(response.status));
        const body = (await response.json()) as { receipts: Arrival[] };
        if (!live) return;
        setArrivals(body.receipts);
        setFailed(false);
      } catch {
        if (live) setFailed(true);
      }
    }
    void look();
    const timer = window.setInterval(() => void look(), 4000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, []);

  // One payment of exactly this amount is this sale's until somebody says otherwise.
  const exact = (arrivals ?? []).filter((arrival) => arrival.amountCents === total);
  useEffect(() => {
    if (chosenByHand) return;
    const only = exact.length === 1 ? exact[0] : undefined;
    onPick(only ? only.eventId : null);
  }, [exact.map((arrival) => arrival.eventId).join(","), total, chosenByHand]);

  const time = (at: string): string =>
    new Date(at).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div>
      <p className="text-[0.813rem] mb-2">
        The customer scans OHC's PayNow QR code and pays <span className="font-semibold">{formatMoney(total)}</span>{" "}
        with <span className="font-semibold">{code}</span> in the payment note. When the payment appears below, or on the
        customer's screen, select Confirm payment received.
      </p>
      <div className="rounded-md border border-defaultborder p-3">
        <p className="font-semibold text-[0.813rem] mb-2">Recent PayNow payments - last 30 minutes</p>
        {failed ? (
          <p className="text-[0.75rem] text-warning mb-0">
            PayNow alerts are unavailable. Check the OCBC app before confirming receipt of payment.
          </p>
        ) : arrivals === null ? (
          <Loading />
        ) : arrivals.length === 0 ? (
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
            <Working label={`Waiting for a payment of S$${formatMoney(total)}`} />
          </p>
        ) : (
          <ul className="list-none mb-0 flex flex-col gap-2 max-h-[12rem] overflow-y-auto">
            {arrivals.map((arrival) => {
              const same = arrival.amountCents === total;
              const chosen = arrival.eventId === picked;
              return (
                <li key={arrival.eventId}>
                  <button
                    type="button"
                    aria-pressed={chosen}
                    className={`w-full flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-start ${
                      chosen
                        ? "border-success bg-success/10"
                        : same
                          ? "border-success/40"
                          : "border-defaultborder opacity-70"
                    }`}
                    onClick={() => {
                      setChosenByHand(true);
                      onPick(chosen ? null : arrival.eventId);
                    }}
                  >
                    <span className="font-semibold text-[1.125rem]">{formatMoney(arrival.amountCents)}</span>
                    <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
                      {time(arrival.paidAt)}
                      {chosen ? <span className="text-success font-semibold ms-2">Selected</span> : same ? <span className="text-success ms-2">Exact amount</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
        {picked
          ? "The selected payment will be linked to this sale."
          : `No payment selected. It will be matched later, from the bank alert when one arrives or from ${code} on the bank statement.`}
      </p>
    </div>
  );
}
